import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const KIND_LABEL = {
  featured_listing: "Featured Listing Sale",
  wallet_topup:     "AI Wallet Deposit",
  ai_topup:         "AI Wallet Deposit",
  escrow:           "Preapproval / Escrow (legacy)",
  deposit:          "Preapproval Deposit (legacy)",
};
const STATUS_TONE = {
  paid:     "text-emerald-600",
  pending:  "text-amber-600",
  refunded: "text-red-600",
  failed:   "text-red-600",
  expired:  "text-muted-foreground",
};

export default function StripeTab() {
  const [ov, setOv] = useState(null);
  const [payments, setPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [refundTx, setRefundTx] = useState(null);
  const [refundReason, setRefundReason] = useState("");

  const loadAll = () => {
    api.get("/admin/stripe/overview").then((r) => setOv(r.data)).catch(() => {});
    api.get("/admin/stripe/config").then((r) => setCfg(r.data)).catch(() => {});
    loadPayments();
    api.get("/admin/stripe/events", { params: { limit: 50 } }).then((r) => setEvents(r.data.items || [])).catch(() => {});
  };
  const loadPayments = () => {
    const params = { limit: 100 };
    if (kind) params.kind = kind;
    if (status) params.status = status;
    api.get("/admin/stripe/payments", { params }).then((r) => setPayments(r.data.items || [])).catch(() => setPayments([]));
  };
  useEffect(loadAll, []);
  useEffect(loadPayments, [kind, status]);

  const saveCfg = async (patch) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/stripe/config", patch);
      setCfg(r.data);
      toast.success("Config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const doRefund = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/stripe/refund/${refundTx.session_id}`, { reason: refundReason });
      if (r.data.manual) toast.info("Recorded for manual Stripe dashboard refund (proxy key)");
      else toast.success("Refund issued");
      setRefundTx(null); setRefundReason("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-stripe-tab">
      {/* KPI Overview */}
      {ov && (
        <section>
          <div className="font-overline text-accent mb-3">Revenue</div>
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-border">
            <Kpi label="Total Revenue" value={fmtMoney(ov.revenue_usd)} tone="ok" testid="stripe-kpi-revenue" />
            <Kpi label="Payments" value={ov.payments_count} testid="stripe-kpi-payments" />
            <Kpi label="Featured Listing Sales" value={`${fmtMoney(ov.featured_revenue)} · ${ov.featured_count}`} testid="stripe-kpi-featured" />
            <Kpi label="AI Wallet Deposits" value={`${fmtMoney(ov.wallet_revenue)} · ${ov.wallet_count}`} testid="stripe-kpi-wallet" />
            <Kpi label="AI Top-ups (ai_topup)" value={`${fmtMoney(ov.ai_revenue ?? 0)} · ${ov.ai_count ?? 0}`} testid="stripe-kpi-ai" />
            <Kpi label="Refunded" value={`${fmtMoney(ov.refunded_usd)} · ${ov.refunded_count}`} tone={ov.refunded_count > 0 ? "alert" : "default"} testid="stripe-kpi-refunded" />
            <Kpi label="Failed / Expired" value={ov.failed_count} tone={ov.failed_count > 0 ? "warn" : "default"} testid="stripe-kpi-failed" />
          </div>
        </section>
      )}

      {/* Products & Pricing */}
      {cfg && <ConfigPanel cfg={cfg} onSave={saveCfg} busy={busy} />}

      {/* Payments Table */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="font-overline text-accent">Payments</div>
          <div className="flex gap-2 flex-wrap">
            <Select value={kind} onValueChange={(v) => setKind(v === "__all__" ? "" : v)}>
              <SelectTrigger className="rounded-none h-9 w-48" data-testid="stripe-kind-filter"><SelectValue placeholder="Any product" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="__all__">Any product</SelectItem>
                <SelectItem value="featured_listing">Featured Listing Sale</SelectItem>
                <SelectItem value="ai_topup">AI Wallet Deposit (ai_topup)</SelectItem>
                <SelectItem value="wallet_topup">AI Wallet Deposit (wallet_topup)</SelectItem>
                <SelectItem value="escrow">Preapproval / Escrow (legacy)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
              <SelectTrigger className="rounded-none h-9 w-48" data-testid="stripe-status-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="__all__">Any status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="rounded-none uppercase text-xs h-9" onClick={loadAll} data-testid="stripe-refresh">Refresh</Button>
          </div>
        </div>
        <div className="border border-border overflow-x-auto" data-testid="stripe-payments-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-muted/50 text-left">
              <tr>
                <Th>When</Th><Th>Product</Th><Th>User</Th><Th>Amount</Th><Th>Status</Th><Th>Session ID</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground text-sm">No matching payments.</td></tr>
              )}
              {payments.map((p) => (
                <tr key={p.session_id} className="border-t border-border" data-testid={`stripe-payment-${p.session_id}`}>
                  <Td className="font-mono text-[10px] whitespace-nowrap">{p.created_at?.slice(0, 16).replace("T", " ")}</Td>
                  <Td className="text-[11px]">{KIND_LABEL[p.kind] || p.kind || "—"}</Td>
                  <Td className="text-[11px] text-muted-foreground">{p.user_email || p.user_id?.slice(-8)}</Td>
                  <Td className="font-mono-data text-[12px]">{fmtMoney(p.amount)}</Td>
                  <Td><span className={`text-[10px] font-mono uppercase ${STATUS_TONE[p.payment_status] || ""}`}>{p.payment_status}</span></Td>
                  <Td className="font-mono text-[10px] text-muted-foreground">{p.session_id?.slice(-16)}</Td>
                  <Td>
                    {p.payment_status === "paid" && !p.refunded && (
                      <Button size="sm" variant="outline" className="rounded-none text-[10px] h-7 uppercase text-red-600"
                              data-testid={`stripe-refund-${p.session_id}`}
                              onClick={() => { setRefundTx(p); setRefundReason(""); }}>
                        Refund
                      </Button>
                    )}
                    {p.refunded && <span className="text-[10px] font-mono uppercase text-muted-foreground">{p.refund_status || "refunded"}</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Events Log */}
      <section>
        <div className="font-overline text-accent mb-3">Webhook Events (last 50)</div>
        <div className="border border-border overflow-x-auto" data-testid="stripe-events-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr><Th>When</Th><Th>Event ID</Th><Th>Type</Th><Th>Session</Th><Th>Processed</Th></tr>
            </thead>
            <tbody>
              {events.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground text-sm">No events logged.</td></tr>}
              {events.map((e) => (
                <tr key={e.event_id} className="border-t border-border" data-testid={`stripe-event-${e.event_id}`}>
                  <Td className="font-mono text-[10px]">{e.at?.slice(0, 16).replace("T", " ")}</Td>
                  <Td className="font-mono text-[10px]">{e.event_id?.slice(-14)}</Td>
                  <Td className="font-mono text-[10px]">{e.event_type}</Td>
                  <Td className="font-mono text-[10px] text-muted-foreground">{e.session_id?.slice(-16) || "—"}</Td>
                  <Td className="font-mono text-[10px]">{e.processed ? <span className="text-emerald-600">yes</span> : <span className="text-muted-foreground">no</span>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {refundTx && (
        <Dialog open onOpenChange={() => setRefundTx(null)}>
          <DialogContent className="rounded-none max-w-md" data-testid="stripe-refund-dialog">
            <DialogHeader><DialogTitle>Refund payment</DialogTitle></DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="text-xs text-muted-foreground">
                {KIND_LABEL[refundTx.kind]} · {fmtMoney(refundTx.amount)} · {refundTx.user_email}
              </div>
              <Label>Reason</Label>
              <Textarea value={refundReason} onChange={(e) => setRefundReason(e.target.value)} className="rounded-none" data-testid="stripe-refund-reason" />
              <p className="text-[11px] text-muted-foreground">
                Note: with the Emergent-managed test key, this records a refund_pending_manual state and reverses internal balances (wallet clawback / featured expiry). Issue the actual Stripe refund from the Stripe dashboard.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setRefundTx(null)}>Cancel</Button>
              <Button className="rounded-none" onClick={doRefund} disabled={busy} data-testid="stripe-refund-confirm">Refund</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-3 py-2 font-overline text-[10px] text-muted-foreground whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2 ${className}`}>{children}</td>;

function Kpi({ label, value, tone, testid }) {
  const t = tone === "ok" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : tone === "alert" ? "text-red-600" : "";
  return (
    <div className="p-5 border-r border-b border-border" data-testid={testid}>
      <div className="font-overline text-[10px] text-muted-foreground">{label}</div>
      <div className={`font-mono-data text-2xl mt-1 font-light ${t}`}>{value}</div>
    </div>
  );
}

function ConfigPanel({ cfg, onSave, busy }) {
  const [price, setPrice] = useState(cfg.featured_price_usd);
  const [duration, setDuration] = useState(cfg.featured_duration_days);
  const [topups, setTopups] = useState((cfg.wallet_topup_options || []).join(", "));
  const [enFeatured, setEnFeatured] = useState(cfg.products_enabled?.featured_listings ?? true);
  const [enWallet, setEnWallet] = useState(cfg.products_enabled?.wallet_topups ?? true);

  useEffect(() => {
    setPrice(cfg.featured_price_usd);
    setDuration(cfg.featured_duration_days);
    setTopups((cfg.wallet_topup_options || []).join(", "));
    setEnFeatured(cfg.products_enabled?.featured_listings ?? true);
    setEnWallet(cfg.products_enabled?.wallet_topups ?? true);
  }, [cfg]);

  const submit = () => {
    const opts = topups.split(",").map((x) => Number(x.trim())).filter((x) => x > 0);
    onSave({
      featured_price_usd: Number(price),
      featured_duration_days: Number(duration),
      wallet_topup_options: opts,
      products_enabled: { featured_listings: enFeatured, wallet_topups: enWallet },
    });
  };

  return (
    <section className="border border-border p-6" data-testid="stripe-config-panel">
      <div className="font-overline text-accent mb-3">Products & Pricing</div>
      <div className="grid md:grid-cols-3 gap-4 text-sm">
        <div>
          <Label className="font-overline text-[10px]">Featured Price (USD)</Label>
          <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-none" data-testid="stripe-cfg-price" />
        </div>
        <div>
          <Label className="font-overline text-[10px]">Featured Duration (days)</Label>
          <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="rounded-none" data-testid="stripe-cfg-duration" />
        </div>
        <div>
          <Label className="font-overline text-[10px]">Wallet Top-Up Amounts (comma-separated USD)</Label>
          <Input value={topups} onChange={(e) => setTopups(e.target.value)} className="rounded-none font-mono" data-testid="stripe-cfg-topups" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
        <label className="flex items-center gap-2" data-testid="stripe-cfg-enable-featured">
          <input type="checkbox" checked={enFeatured} onChange={(e) => setEnFeatured(e.target.checked)} /> Featured Listings enabled
        </label>
        <label className="flex items-center gap-2" data-testid="stripe-cfg-enable-wallet">
          <input type="checkbox" checked={enWallet} onChange={(e) => setEnWallet(e.target.checked)} /> Wallet Top-ups enabled
        </label>
        <Button className="rounded-none uppercase text-xs h-9 ml-auto" onClick={submit} disabled={busy} data-testid="stripe-cfg-save">Save Configuration</Button>
      </div>
    </section>
  );
}
